import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { CARD, CARD2, CARD3, BD, TX, TX2, GR, BL, YL, RD, PU } from "../../utils/constants";
import { scoreColor, gradeLabel, biasColor, getInvestmentAdvice, sanitizeDecimalInput, alertLabel,
         NA, isNum, fmtPrice, fmtPct, fmtNum, fmtBig, fmtDate,
         fmtMoney, fmtMoneySigned, fmtShares } from "../../utils/formatUtils";
import { buildTradeRow, tradeShares } from "../../utils/tradeStats";

const API_BASE = process.env.REACT_APP_API_URL || "";

function useCountUp(target, duration = 600) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    setCount(0);
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return count;
}

function MiniChart({ stock }) {
  const change = Number(stock.change || 0);
  const fiveDay = Number(stock.fiveDayReturn ?? change);
  const twentyDay = Number(stock.twentyDayReturn ?? fiveDay);
  const seed = stock.symbol.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0);
  const points = Array.from({ length: 18 }, (_, i) => {
    const t = i / 17;
    const trend = (twentyDay / 100) * t;
    const pulse = Math.sin((t * Math.PI * 3.2) + seed) * 0.018;
    const lateMove = (fiveDay / 100) * Math.max(0, t - 0.66) * 1.4;
    return 100 * (1 + trend + pulse + lateMove);
  });
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = Math.max(max - min, 1);
  const chartPoints = points.map((value, i) => {
    const x = 18 + (i / (points.length - 1)) * 524;
    const y = 178 - ((value - min) / range) * 130;
    return { x, y };
  });
  const line = chartPoints.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
  const area = `${line} L 542 190 L 18 190 Z`;
  const lineColor = change >= 0 ? GR : RD;
  const gradientId = `chartFill${stock.symbol.replace(/[^a-zA-Z0-9]/g, "")}`;

  return (
    <div style={{ borderRadius: 10, overflow: "hidden", border: `1px solid ${BD}`, background: CARD2, marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 13px", borderBottom: `1px solid ${BD}` }}>
        <div>
          <div style={{ fontSize: 8, color: TX2, letterSpacing: 2, marginBottom: 3 }}>SCANNER CHART</div>
          <div style={{ fontSize: 10, color: TX }}>Local trend view for research context</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 8, color: TX2, letterSpacing: 1 }}>5-DAY</div>
          <div style={{ fontSize: 14, color: fiveDay >= 0 ? GR : RD, fontWeight: 800 }}>{fiveDay >= 0 ? "+" : ""}{fiveDay.toFixed(2)}%</div>
        </div>
      </div>
      <svg viewBox="0 0 560 210" role="img" aria-label={`${stock.symbol} scanner trend chart`} style={{ width: "100%", height: 230, display: "block" }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={lineColor} stopOpacity="0.24" />
            <stop offset="100%" stopColor={lineColor} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {[42, 80, 118, 156].map((y) => (
          <line key={y} x1="18" x2="542" y1={y} y2={y} stroke={BD} strokeOpacity="0.72" strokeDasharray="4 7" />
        ))}
        {[18, 150, 280, 410, 542].map((x) => (
          <line key={x} x1={x} x2={x} y1="28" y2="190" stroke={BD} strokeOpacity="0.38" />
        ))}
        <path d={area} fill={`url(#${gradientId})`} />
        <path d={line} fill="none" stroke={lineColor} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        {chartPoints.filter((_, i) => i % 4 === 0 || i === chartPoints.length - 1).map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="4" fill={CARD} stroke={lineColor} strokeWidth="2" />
        ))}
        <text x="20" y="24" fill={TX2} fontSize="10" letterSpacing="2">20D RANGE</text>
        <text x="392" y="24" fill={lineColor} fontSize="12" fontWeight="700">{twentyDay >= 0 ? "+" : ""}{twentyDay.toFixed(2)}% 20D</text>
        <text x="20" y="205" fill={TX2} fontSize="9">Educational visualization. Verify on live charts before making decisions.</text>
      </svg>
    </div>
  );
}


export default function StockDetailModal({
  stock,
  onClose,
  watchlist,
  onToggleWL,
  alerts, setAlerts,
  alertInput, setAlertInput,
  newsMap,
  loadingDetail,
  investments,
  onAddInvestment,
  onRemoveInvestment,
}) {
  const displayScore = useCountUp(stock?.score || 0, 500);
  const isMobile = useMemo(() => window.innerWidth < 768, []);
  const [investPrice, setInvestPrice] = useState("");
  // Cash put into the position. Optional — leaving it blank tracks the trade
  // exactly as before, by entry price only.
  const [investAmount, setInvestAmount] = useState("");
  // true = watching, false = not, "saving" = request in flight
  const [watchSetupState, setWatchSetupState] = useState(false);
  // Clear both entry fields when the modal switches stocks, so an amount typed
  // for one symbol can't be submitted against another.
  useEffect(() => { setInvestPrice(""); setInvestAmount(""); }, [stock?.symbol]);
  useEffect(() => {
    // Reflect whether this symbol already has a watched setup stored.
    const existing = alerts?.[stock?.symbol];
    setWatchSetupState(!!(existing && typeof existing === "object" && existing.watchSetup));
  }, [stock?.symbol, alerts]);

  if (!stock) return null;
  return (
    <AnimatePresence>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="ts-scan-shell"
      style={{ position: "fixed", inset: 0, background: "rgba(5,8,20,0.86)", display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center", zIndex: 1000, padding: isMobile ? 0 : 16 }}
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 60, opacity: 0, scale: 0.97 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ duration: 0.22, ease: "easeOut" }}
        className="ts-panel"
        role="dialog"
        aria-modal="true"
        aria-label={`${stock.symbol} detail`}
        style={{ background: CARD, border: `1px solid ${scoreColor(stock.score)}40`, borderRadius: isMobile ? "16px 16px 0 0" : 14, padding: 22, maxWidth: isMobile ? "100vw" : 620, width: "100%", maxHeight: isMobile ? "95vh" : "93vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
              <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 24, fontWeight: 800, color: TX }}>{stock.symbol}</div>
              <span className="star" onClick={() => onToggleWL(stock.symbol)} style={{ fontSize: 16, color: watchlist.includes(stock.symbol) ? YL : TX2 }}>{watchlist.includes(stock.symbol) ? "★" : "☆"}</span>
            </div>
            <div style={{ fontSize: 10, color: TX2 }}>{stock.name} · {stock.sector}</div>
            <div style={{ fontSize: 12, color: TX, marginTop: 4 }}>
              {fmtPrice(stock.price)}{" "}
              <span style={{ color: !Number.isFinite(stock.change) ? TX2 : stock.change >= 0 ? GR : RD, fontWeight: 600 }}>
                {fmtPct(stock.change)}
              </span>
            </div>
            {stock.fiveDayReturn != null && (
              <div style={{ fontSize: 9, color: TX2, marginTop: 2 }}>
                5-day: <span style={{ color: stock.fiveDayReturn >= 0 ? GR : RD, fontWeight: 600 }}>{stock.fiveDayReturn >= 0 ? "+" : ""}{stock.fiveDayReturn.toFixed(2)}%</span>
                {stock.twentyDayReturn != null && (<>{"  "}20-day: <span style={{ color: stock.twentyDayReturn >= 0 ? GR : RD, fontWeight: 600 }}>{stock.twentyDayReturn >= 0 ? "+" : ""}{stock.twentyDayReturn.toFixed(2)}%</span></>)}
              </div>
            )}
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 32, fontWeight: 800, fontFamily: "'Space Grotesk',sans-serif", color: scoreColor(stock.score), lineHeight: 1 }}>{displayScore}</div>
            <div style={{ fontSize: 8, color: scoreColor(stock.score), letterSpacing: 2, marginTop: 2 }}>{gradeLabel(stock)}</div>
            {/* How much of the intended analysis actually ran. Without this, a
                stock with no fundamentals can post the same score as one that
                passed both technical and fundamental checks. */}
            {stock.peerLabel && (
              <div style={{ fontSize: 8, color: BL, marginTop: 3 }} title={`Based on ${stock.peerSampleSize?.toLocaleString?.() ?? "?"} historical setups of similar volatility. Descriptive ranking, not a prediction.`}>
                {stock.peerLabel}
              </div>
            )}
            {Number.isFinite(stock.dataCoverage) && (
              <div style={{ fontSize: 8, color: stock.dataCoverage >= 70 ? TX2 : YL, marginTop: 3 }}>
                Data Coverage: {stock.dataCoverage}%
              </div>
            )}
            <div style={{ fontSize: 9, marginTop: 4, color: biasColor(stock.tomorrowBias), fontWeight: 600 }}>
              Technical Bias: {stock.technicalBias ?? stock.tomorrowBias}
            </div>
            {/* "Confidence: 100/100" invites reading as a 100% chance of going
                up. It measures how much the signals agree with each other, and
                is named for what it is. */}
            {Number.isFinite(stock.biasConfidence) && (
              <div style={{ fontSize: 8, color: TX2, marginTop: 2 }}>Signal Agreement: {stock.biasConfidence}/100</div>
            )}
            {stock.biasBasis && (
              <div style={{ fontSize: 7, color: TX2, marginTop: 3, maxWidth: 190, textAlign: "right", lineHeight: 1.45 }}>
                {stock.biasBasis}
              </div>
            )}
            {loadingDetail && <div style={{ fontSize: 8, color: TX2, marginTop: 4 }}>loading details...</div>}
          </div>
        </div>

        <MiniChart stock={stock} />

        {/* Why the algorithm picked this */}
        <div style={{ background: CARD2, borderRadius: 10, padding: 14, marginBottom: 14 }}>
          <div style={{ fontSize: 9, color: TX2, letterSpacing: 2, marginBottom: 12 }}>WHY THE ALGORITHM PICKED THIS</div>
          {[
            { label: "MOMENTUM", value: stock.momentum, color: PU, reasons: stock.momentumReasons },
            { label: "POSITION IN RANGE", value: stock.positionScore ?? stock.volumeScore, color: BL, reasons: stock.positionReasons ?? [] },
            { label: "TREND", value: stock.trend, color: GR, reasons: stock.trendReasons },
            { label: "FUNDAMENTALS", value: stock.fundamentalsScore ?? stock.volumeScore, color: YL, reasons: stock.fundamentalsReasons ?? stock.volumeReasons },
          ].map(({ label, value, color, reasons }) => (
            <div key={label} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                <span style={{ fontSize: 9, color, fontWeight: 600, letterSpacing: 1 }}>{label}</span>
                <span style={{ fontSize: 13, fontWeight: 800, color }}>{value}<span style={{ fontSize: 9, color: TX2 }}>/100</span></span>
              </div>
              <div style={{ height: 4, background: CARD3, borderRadius: 3, marginBottom: 6 }}>
                <div style={{ height: "100%", width: `${value}%`, background: color, borderRadius: 3 }} />
              </div>
              {reasons?.map((r, i) => (
                <div key={i} style={{ fontSize: 9, color: TX2, padding: "3px 8px", background: CARD3, borderRadius: 4, marginBottom: 3, borderLeft: `2px solid ${color}50` }}>{r}</div>
              ))}
            </div>
          ))}

          {stock.candleData && (
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", padding: "8px 10px", background: CARD3, borderRadius: 6 }}>
              {stock.candleData.ma5  && <span style={{ fontSize: 8, color: TX2 }}>MA5:  <span style={{ color: TX }}>${stock.candleData.ma5}</span></span>}
              {stock.candleData.ma20 && <span style={{ fontSize: 8, color: TX2 }}>MA20: <span style={{ color: TX }}>${stock.candleData.ma20}</span></span>}
              {stock.candleData.ma50 && <span style={{ fontSize: 8, color: TX2 }}>MA50: <span style={{ color: TX }}>${stock.candleData.ma50}</span></span>}
              {stock.fiveDayReturn   != null && <span style={{ fontSize: 8, color: TX2 }}>5-day: <span style={{ color: stock.fiveDayReturn >= 0 ? GR : RD }}>{stock.fiveDayReturn >= 0 ? "+" : ""}{stock.fiveDayReturn.toFixed(2)}%</span></span>}
              {stock.twentyDayReturn != null && <span style={{ fontSize: 8, color: TX2 }}>20-day: <span style={{ color: stock.twentyDayReturn >= 0 ? GR : RD }}>{stock.twentyDayReturn >= 0 ? "+" : ""}{stock.twentyDayReturn.toFixed(2)}%</span></span>}
            </div>
          )}

          {stock.extraReasons?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 9, color: TX2, letterSpacing: 2, marginBottom: 6 }}>EXTRA SIGNALS</div>
              {stock.extraReasons.map((r, i) => {
                const isRisk = r.includes("EARNINGS RISK") || r.includes("selling");
                const isGood = r.includes("buying") || r.includes("Post-earnings") || r.includes("growing");
                const col    = isRisk ? RD : isGood ? GR : YL;
                return (
                  <div key={i} style={{ fontSize: 9, color: TX2, padding: "3px 8px", background: CARD3, borderRadius: 4, marginBottom: 3, borderLeft: `2px solid ${col}` }}>{r}</div>
                );
              })}
            </div>
          )}
        </div>

        {/* Risk / Reward */}
        {stock.riskReward && (
          <div style={{ background: CARD2, borderRadius: 10, padding: 14, marginBottom: 14 }}>
            <div style={{ fontSize: 9, color: TX2, letterSpacing: 2, marginBottom: 11 }}>RISK / REWARD SETUP (SWING)</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
              {[
                { l: "ENTRY", v: fmtPrice(stock.riskReward.entry), c: TX },
                // Stop distance sits next to the stop price: a level alone
                // hides whether it's a 4% stop or a 40% one.
                { l: "STOP LOSS", v: Number.isFinite(stock.riskReward.stopPct)
                    ? `${fmtPrice(stock.riskReward.stopLoss)} (−${stock.riskReward.stopPct.toFixed(1)}%)`
                    : fmtPrice(stock.riskReward.stopLoss),
                  c: stock.riskReward.stopPct > 12 ? RD : RD },
                { l: "TARGET", v: fmtPrice(stock.riskReward.target), c: GR },
                { l: "R:R RATIO", v: Number.isFinite(stock.riskReward.rr) ? `1:${stock.riskReward.rr.toFixed(1)}` : NA, c: YL },
              ].map((item) => (
                <div key={item.l} style={{ background: CARD3, borderRadius: 7, padding: "9px 10px" }}>
                  <div style={{ fontSize: 7, color: TX2, letterSpacing: 1, marginBottom: 3 }}>{item.l}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: item.c }}>{item.v}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Day stats — missing values render as N/A, never as $0.00 */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 7, marginBottom: 14 }}>
          {[
            { l: "DAY HIGH",   v: fmtPrice(stock.high) },
            { l: "DAY LOW",    v: fmtPrice(stock.low) },
            { l: "PREV CLOSE", v: fmtPrice(stock.prevClose) },
          ].map((item) => (
            <div key={item.l} style={{ background: CARD2, borderRadius: 7, padding: "7px 10px" }}>
              <div style={{ fontSize: 7, color: TX2, letterSpacing: 1, marginBottom: 2 }}>{item.l}</div>
              <div style={{ fontSize: 11, fontWeight: 600, color: item.v === NA ? TX2 : TX }}>{item.v}</div>
            </div>
          ))}
        </div>

        {/* Overextension and setup-quality warnings.
            Shown before the levels, because "the stop is 12% away and this has
            already run 60%" changes how the numbers below should be read. */}
        {(stock.extensionReasons?.length > 0 || stock.setupDisqualifiers?.length > 0) && (
          <div style={{ background: CARD2, borderRadius: 10, padding: 12, marginBottom: 13, border: `1px solid ${YL}22` }}>
            <div style={{ fontSize: 9, color: YL, letterSpacing: 2, fontWeight: 600, marginBottom: 7 }}>SETUP QUALITY</div>
            {stock.extensionReasons?.map((r, i) => (
              <div key={`ext-${i}`} style={{ fontSize: 9, color: TX2, padding: "3px 8px", background: CARD3, borderRadius: 4, marginBottom: 3, borderLeft: `2px solid ${YL}` }}>{r}</div>
            ))}
            {stock.setupDisqualifiers?.length > 0 && (
              <div style={{ fontSize: 8, color: TX2, marginTop: 6 }}>
                Not eligible for TOP SETUP: {stock.setupDisqualifiers.join(", ")}.
              </div>
            )}
          </div>
        )}

        {/* Watch setup — one toggle that monitors the whole setup */}
        {(() => {
          const [watching, setWatching] = [watchSetupState, setWatchSetupState];
          const rr = stock.riskReward;
          const canWatch = rr && Number.isFinite(rr.entry);
          if (!canWatch) return null;
          return (
            <div style={{ background: CARD2, borderRadius: 10, padding: 12, marginBottom: 13, border: `1px solid ${GR}18` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 9, color: GR, letterSpacing: 2, fontWeight: 600, marginBottom: 3 }}>WATCH SETUP</div>
                  <div style={{ fontSize: 8, color: TX2 }}>
                    Monitors entry zone, target {fmtPrice(rr.target)}, stop {fmtPrice(rr.stopLoss)}, score changes and setup invalidation.
                  </div>
                </div>
                <button
                  className="btn"
                  disabled={watching === "saving"}
                  onClick={async () => {
                    setWatching("saving");
                    try {
                      if (watching === true) {
                        await axios.delete(`${API_BASE}/api/user-data/alerts/${encodeURIComponent(stock.symbol)}`);
                        setWatching(false);
                      } else {
                        await axios.put(`${API_BASE}/api/user-data/alerts/${encodeURIComponent(stock.symbol)}`, {
                          watchSetup: true,
                          entryPrice: rr.entry,
                          targetPrice: rr.target,
                          stopPrice: rr.stopLoss,
                          baselineScore: stock.score,
                          baselineBias: stock.technicalBias ?? stock.tomorrowBias,
                        });
                        setWatching(true);
                      }
                    } catch {
                      setWatching(watching === true);
                    }
                  }}
                  style={{ background: watching === true ? GR + "20" : CARD3, border: `1px solid ${watching === true ? GR + "50" : BD}`,
                           borderRadius: 6, padding: "7px 13px", color: watching === true ? GR : TX2, fontSize: 9, fontWeight: 700, whiteSpace: "nowrap" }}
                >
                  {watching === "saving" ? "SAVING..." : watching === true ? "✓ WATCHING" : "WATCH SETUP"}
                </button>
              </div>
            </div>
          );
        })()}

        {/* Event warnings — context and risk only, never a directional signal */}
        {stock.eventWarnings?.length > 0 && (
          <div style={{ marginBottom: 13 }}>
            {stock.eventWarnings.map((w, i) => {
              const col = w.severity === "high" ? RD : w.severity === "medium" ? YL : TX2;
              return (
                <div key={`${w.type}-${i}`} style={{ display: "flex", gap: 8, alignItems: "flex-start", background: CARD2,
                       borderRadius: 7, padding: "8px 11px", marginBottom: 5, borderLeft: `3px solid ${col}` }}>
                  <span style={{ fontSize: 11, color: col }}>⚠</span>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: col }}>{w.label}</div>
                    <div style={{ fontSize: 8, color: TX2, marginTop: 1 }}>{w.note}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Fundamentals — company financials, deliberately separate from price position */}
        <div style={{ background: CARD2, borderRadius: 10, padding: 13, marginBottom: 13, border: `1px solid ${YL}18` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 9 }}>
            <span style={{ fontSize: 8, color: YL, letterSpacing: 2, fontWeight: 600 }}>FUNDAMENTALS</span>
            {stock.fundamentalsEvaluated === false && (
              <span style={{ fontSize: 7, color: TX2 }}>not evaluated for this stock</span>
            )}
          </div>
          {(() => {
            const f = stock.fundamentals || {};
            const rows = [
              { l: "REVENUE TTM",     v: fmtBig(f.revenueTTM) },
              { l: f.latestQuarterLabel ? `REVENUE (${f.latestQuarterLabel})` : "LATEST QUARTER", v: fmtBig(f.latestQuarterRevenue) },
              { l: "REVENUE GROWTH YoY", v: fmtPct(f.revenueGrowthYoy) },
              { l: "EPS TTM",         v: fmtNum(f.epsTTM) },
              { l: "EPS GROWTH YoY",  v: fmtPct(f.epsGrowthYoy) },
              { l: "NET MARGIN",      v: fmtPct(f.netProfitMargin) },
              { l: "P/E RATIO",       v: fmtNum(f.peRatio) },
              { l: "MARKET CAP",      v: fmtBig(f.marketCap) },
              { l: "NEXT EARNINGS",   v: fmtDate(f.nextEarningsDate) },
            ];
            return (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 7 }}>
                  {rows.map((r) => (
                    <div key={r.l} style={{ background: CARD3, borderRadius: 6, padding: "6px 9px" }}>
                      <div style={{ fontSize: 7, color: TX2, letterSpacing: 0.5, marginBottom: 2 }}>{r.l}</div>
                      <div style={{ fontSize: 10, fontWeight: 600, color: r.v === NA ? TX2 : TX }}>{r.v}</div>
                    </div>
                  ))}
                </div>
                {(() => {
                  const rg = f.meta?.revenueGrowthYoy;
                  const periods = rg?.comparedPeriods?.filter(Boolean);
                  return (
                    <div style={{ fontSize: 7, color: TX2, marginTop: 7, lineHeight: 1.5 }}>
                      {rg && periods?.length === 2 && (
                        <div>
                          Revenue growth compares {periods[0]} with {periods[1]} ({rg.method}, source: {rg.source}).
                        </div>
                      )}
                      {rg && rg.basis === "ttm" && (
                        <div>Revenue growth is a trailing-twelve-month figure reported by {rg.source}.</div>
                      )}
                      {f.fetchedAt && <div>Fundamentals fetched {fmtDate(f.fetchedAt)}.</div>}
                      {f.dataStatus?.finnhub === "error" || f.dataStatus?.fmp === "error" ? (
                        <div style={{ color: YL }}>
                          Some fields are temporarily unavailable (provider error) and will retry shortly.
                        </div>
                      ) : (f.dataStatus?.finnhub === "restricted" || f.dataStatus?.fmp === "restricted") ? (
                        <div style={{ color: TX2 }}>
                          Some fields aren't covered for this symbol by the current data plan.
                        </div>
                      ) : null}
                    </div>
                  );
                })()}
              </>
            );
          })()}
        </div>

        {/* My Investment */}
        <div style={{ background: CARD2, borderRadius: 10, padding: 13, marginBottom: 13, border: `1px solid ${BL}18` }}>
          <div style={{ fontSize: 8, color: BL, letterSpacing: 2, marginBottom: 9, fontWeight: 600 }}>MY INVESTMENT</div>
          {(() => {
            const inv = investments?.[stock.symbol];
            if (!inv) {
              const previewShares = tradeShares(Number(investAmount), Number(investPrice));
              return (
                <div>
                  <div style={{ display: "flex", gap: 7, alignItems: "center" }}>
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder={`Price you paid (e.g. ${(stock.price || 0).toFixed(2)})`}
                      value={investPrice}
                      onChange={(e) => setInvestPrice(sanitizeDecimalInput(e.target.value))}
                      style={{ flex: 1, background: CARD3, border: `1px solid ${BD}`, borderRadius: 5, padding: "7px 9px", color: TX, fontSize: 10 }}
                    />
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="Amount invested (e.g. 1000)"
                      value={investAmount}
                      onChange={(e) => setInvestAmount(sanitizeDecimalInput(e.target.value))}
                      style={{ flex: 1, background: CARD3, border: `1px solid ${BD}`, borderRadius: 5, padding: "7px 9px", color: TX, fontSize: 10 }}
                    />
                    <button
                      className="btn"
                      onClick={() => {
                        if (investPrice) {
                          onAddInvestment(stock.symbol, investPrice, { amountInvested: Number(investAmount) });
                          setInvestPrice("");
                          setInvestAmount("");
                        }
                      }}
                      style={{ background: BL + "18", border: `1px solid ${BL}40`, borderRadius: 5, padding: "7px 13px", color: BL, fontSize: 9, fontWeight: 700, flexShrink: 0 }}
                    >
                      Track
                    </button>
                  </div>
                  <div style={{ fontSize: 7, color: TX2, marginTop: 5 }}>
                    {isNum(previewShares)
                      ? `${fmtMoney(Number(investAmount))} at ${fmtPrice(Number(investPrice))} — ${fmtShares(previewShares)}. Profit and loss will be shown in dollars.`
                      : "Amount is optional — add it to see profit and loss in dollars, not just percent."}
                  </div>
                </div>
              );
            }
            const advice = getInvestmentAdvice(inv.entryPrice, stock.price, stock);
            const verdictColor = advice.verdict === "SELL" ? RD : GR;
            const row = buildTradeRow(stock.symbol, inv, stock);
            const up = advice.pnlPct >= 0;
            return (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 9, gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 8, color: TX2 }}>YOUR ENTRY PRICE</div>
                    <div style={{ fontSize: 13, color: TX, fontWeight: 700 }}>${inv.entryPrice.toFixed(2)}</div>
                    {/* Cash view. Absent on positions tracked before an amount
                        was recorded — those keep showing the percentage alone
                        rather than a fabricated $0.00. */}
                    {isNum(row.amountInvested) && (
                      <div style={{ fontSize: 8, color: TX2, marginTop: 4, lineHeight: 1.6 }}>
                        <div>invested {fmtMoney(row.amountInvested)} · {fmtShares(row.shares)}</div>
                        <div>now worth {fmtMoney(row.currentValue)}</div>
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: "right" }}>
                    {/* The dollar figure leads when it is known — it is the
                        number the user actually asked the app for. */}
                    {isNum(row.pnlAmount) ? (
                      <>
                        <div style={{ fontSize: 20, fontWeight: 800, color: up ? GR : RD, fontFamily: "'Space Grotesk',sans-serif" }}>
                          {fmtMoneySigned(row.pnlAmount)}
                        </div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: up ? GR : RD }}>
                          {up ? "+" : ""}{advice.pnlPct.toFixed(2)}%
                        </div>
                      </>
                    ) : (
                      <div style={{ fontSize: 16, fontWeight: 800, color: up ? GR : RD }}>
                        {up ? "+" : ""}{advice.pnlPct.toFixed(2)}%
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ padding: "9px 11px", background: verdictColor + "12", border: `1px solid ${verdictColor}40`, borderRadius: 7 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: verdictColor, letterSpacing: 1, marginBottom: 3 }}>{advice.verdict}</div>
                  <div style={{ fontSize: 9, color: TX2, lineHeight: 1.5 }}>{advice.reason}</div>
                </div>
                <button className="btn" onClick={() => onRemoveInvestment(stock.symbol)} style={{ width: "100%", marginTop: 8, background: CARD3, border: `1px solid ${BD}`, borderRadius: 6, padding: "7px", color: TX2, fontSize: 8, letterSpacing: 1 }}>
                  STOP TRACKING
                </button>
              </div>
            );
          })()}
        </div>

        {/* Price alert */}
        <div style={{ background: CARD2, borderRadius: 9, padding: 12, marginBottom: 12 }}>
          <div style={{ fontSize: 8, color: TX2, letterSpacing: 2, marginBottom: 7 }}>PRICE ALERT</div>
          <div style={{ display: "flex", gap: 7 }}>
            <input type="text" inputMode="decimal" placeholder={`Target (e.g. $${((stock.price || 0) * 1.05).toFixed(0)})`} value={alertInput} onChange={(e) => setAlertInput(sanitizeDecimalInput(e.target.value))} style={{ flex: 1, background: CARD3, border: `1px solid ${BD}`, borderRadius: 5, padding: "6px 9px", color: TX, fontSize: 10 }} />
            <button className="btn" onClick={() => { if (alertInput) { setAlerts((p) => ({ ...p, [stock.symbol]: alertInput })); setAlertInput(""); } }} style={{ background: GR + "18", border: `1px solid ${GR}40`, borderRadius: 5, padding: "6px 12px", color: GR, fontSize: 9, fontWeight: 700 }}>Set</button>
            {alerts[stock.symbol] && <button className="btn" onClick={() => setAlerts((p) => { const n = { ...p }; delete n[stock.symbol]; return n; })} style={{ background: RD + "12", border: `1px solid ${RD}40`, borderRadius: 5, padding: "6px 9px", color: RD, fontSize: 9 }}>✕</button>}
          </div>
          {alertLabel(alerts[stock.symbol]) && <div style={{ fontSize: 8, color: YL, marginTop: 5 }}>Alert at {alertLabel(alerts[stock.symbol])}</div>}
        </div>

        {/* Do Your Research */}
        {stock.score >= 75 && (
          <div style={{ background: CARD2, border: `1px solid ${stock.score === 100 ? RD + "60" : YL + "40"}`, borderRadius: 9, padding: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: stock.score === 100 ? RD : YL, letterSpacing: 1, marginBottom: 9 }}>
              {stock.score === 100 ? "EXTREME SIGNAL - VERIFY BEFORE ACTING" : "DO YOUR RESEARCH FIRST"}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              {[
                { label: "Google News", color: BL, href: `https://news.google.com/search?q=${stock.symbol}+stock` },
                { label: "Seeking Alpha", color: GR, href: `https://seekingalpha.com/symbol/${stock.symbol}` },
                { label: "Reddit WSB", color: "#ff6314", href: `https://www.reddit.com/r/wallstreetbets/search/?q=${stock.symbol}&sort=new` },
                { label: "Earnings Whispers", color: YL, href: `https://www.earningswhispers.com/stocks/${stock.symbol.toLowerCase()}` },
              ].map((link) => (
                <a key={link.label} href={link.href} target="_blank" rel="noreferrer"
                  style={{ display: "block", textAlign: "center", padding: "7px 10px", background: CARD3, border: `1px solid ${BD}`, borderRadius: 6, color: link.color, fontSize: 9, fontWeight: 600, textDecoration: "none", letterSpacing: 0.5 }}>
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* News */}
        {newsMap[stock.symbol]?.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 8, color: TX2, letterSpacing: 2, marginBottom: 7 }}>LATEST NEWS</div>
            {newsMap[stock.symbol].map((n, i) => (
              <a key={i} href={n.url} target="_blank" rel="noreferrer" style={{ display: "block", fontSize: 9, color: BL, marginBottom: 4, textDecoration: "none", padding: "6px 9px", background: CARD2, borderRadius: 5, borderLeft: `2px solid ${BL}40` }}>
                {(n.headline || "").slice(0, 90)}...
              </a>
            ))}
          </div>
        )}

        {/* External links */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 7, marginBottom: 10 }}>
          <a href={`https://finance.yahoo.com/quote/${stock.symbol}`} target="_blank" rel="noreferrer" style={{ textAlign: "center", background: BL + "10", border: `1px solid ${BL}30`, borderRadius: 7, padding: "8px", color: BL, textDecoration: "none", fontSize: 9, fontWeight: 600 }}>Yahoo Finance</a>
          <a href={`https://www.tradingview.com/chart/?symbol=${stock.symbol}`} target="_blank" rel="noreferrer" style={{ textAlign: "center", background: GR + "10", border: `1px solid ${GR}30`, borderRadius: 7, padding: "8px", color: GR, textDecoration: "none", fontSize: 9, fontWeight: 600 }}>TradingView</a>
          <a href={`https://stockanalysis.com/stocks/${stock.symbol.toLowerCase()}/`} target="_blank" rel="noreferrer" style={{ textAlign: "center", background: YL + "10", border: `1px solid ${YL}30`, borderRadius: 7, padding: "8px", color: YL, textDecoration: "none", fontSize: 9, fontWeight: 600 }}>Stock Analysis</a>
        </div>

        <button className="btn" onClick={onClose} style={{ width: "100%", background: CARD2, border: `1px solid ${BD}`, borderRadius: 7, padding: "8px", color: TX2, fontSize: 8, letterSpacing: 2 }}>CLOSE</button>
      </motion.div>
    </motion.div>
    </AnimatePresence>
  );
}
